import {
  Component,
  ElementRef,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  Input,
  TemplateRef,
  ChangeDetectorRef,
  AfterViewInit,
  Renderer2,
  forwardRef,
  OnDestroy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DragDrop, DropListRef, DragRef, moveItemInArray } from '@angular/cdk/drag-drop';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CDKDropEvent } from '../phx-drag-and-drop.types';
import { PHX_SPACING } from '../../../constants';
import { PhxSpacing } from '../../../types';

@Component({
  selector: 'phx-drop-list',
  templateUrl: './phx-drop-list.component.html',
  styleUrls: ['./phx-drop-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhxDropListComponent),
      multi: true,
    },
  ],
  host: {
    class: 'phx-drop-list',
    '[class.phx-drop-list-gap-md]': 'phxGap === PHX_SPACING.MD',
    '[class.phx-drop-list-gap-mdl]': 'phxGap === PHX_SPACING.MDL',
    '[class.phx-drop-list-gap-lg]': 'phxGap === PHX_SPACING.LG',
    '[class.phx-drop-list-gap-xl]': 'phxGap === PHX_SPACING.XL',
  },
})
export class PhxDropListComponent<T> implements AfterViewInit, OnDestroy, ControlValueAccessor {
  /**
   * Template reference for the drag item.
   */
  @Input() phxDragItemTemplate: TemplateRef<T>;

  /**
   * Gap size between items in the drop list.
   */
  @Input() phxGap: PhxSpacing = PHX_SPACING.MD;

  /**
   * Native parent element that is scrollable (if any).
   * This is used to enable auto-scrolling with respect to parent when dragging items.
   */

  @Input() phxScrollContainer: HTMLElement;

  /**
   * Sets the view mode of the drop list.
   */
  @Input()
  set phxViewMode(value: boolean) {
    this.isViewMode$.next(value);
  }

  /**
   * Gets the view mode of the drop list.
   */
  get phxViewMode(): boolean {
    return this.isViewMode$.getValue();
  }

  public nativeElement: HTMLElement;
  public readonly isViewMode$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly PHX_SPACING = PHX_SPACING;

  private _phxList: T[] = [];
  private _dropList: DropListRef;
  private _dragRefs: DragRef[] = [];
  private _onChange: Function = () => {};
  private _onTouched: Function = () => {};
  private _destroy$: Subject<void> = new Subject();
  private readonly scrollSpeed = 5; // Speed of auto-scrolling in pixels per frame

  constructor(private _dragDrop: DragDrop, private _elementRef: ElementRef, private _cdr: ChangeDetectorRef, private _renderer: Renderer2) {}

  public ngAfterViewInit(): void {
    this.nativeElement = this._elementRef.nativeElement;
    this.initializeDropList();
    this.setupDropListSubscriptions();
    this.setupViewModeSubscriptions();
    this._dropList.withItems(this._dragRefs);
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._dropList.dispose();
  }

  public writeValue(value: T[]): void {
    this._phxList = value || [];
    this._cdr.markForCheck();
  }

  public registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  /**
   * Initializes the drop list using the DragDrop service.
   */
  private initializeDropList(): void {
    this._dropList = this._dragDrop.createDropList(this._elementRef);
    this._dropList.autoScrollStep = this.scrollSpeed;
    if(this.phxScrollContainer){
      this._dropList.withScrollableParents([this.phxScrollContainer]);
    }
  }

  /**
   * Sets up subscriptions for the drop list events.
   */
  private setupDropListSubscriptions(): void {
    this._dropList.beforeStarted.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this._renderer.addClass(this.nativeElement, 'phx-drop-list-dragging');
    });

    this._dropList.dropped.pipe(takeUntil(this._destroy$)).subscribe((event: CDKDropEvent) => {
      this.drop(event);
      this._renderer.removeClass(this.nativeElement, 'phx-drop-list-dragging');
    });
  }

  /**
   * Sets up subscriptions for the view mode changes.
   */
  private setupViewModeSubscriptions(): void {
    this.isViewMode$.pipe(takeUntil(this._destroy$)).subscribe((isViewMode: boolean) => {
      this._dropList.disabled = isViewMode;
    });
  }

  /**
   * Registers a drag item with the drop list. It is used in drag item component to register itself with the drop list.
   * @param dragRef - The DragRef instance to register.
   */
  public registerDragItem(dragRef: DragRef) {
    this._dragRefs.push(dragRef);
    if (this._dragRefs.length === this._phxList.length) {
      this._dropList.withItems(this._dragRefs);
    }
  }

  /**
   * Unregisters a drag item from the drop list. It is used in drag item component to unregister itself from the drop list.
   * @param dragRef - The DragRef instance to unregister.
   */
  public unregisterDragItem(dragRef: DragRef) {
    const dragRefIndex = this._dragRefs.indexOf(dragRef);
    if (dragRefIndex > -1) {
      this._dragRefs.splice(dragRefIndex, 1);
      this._dropList.withItems(this._dragRefs);
    }
  }

  /**
   * Gets the list of items in the drop list.
   */
  public get phxList(): T[] {
    return this._phxList;
  }

  /**
   * Handles the drop event and updates the list of items.
   * @param event - The CDKDropEvent containing drop information.
   */
  private drop(event: CDKDropEvent) {
    this.moveListItem(event.currentIndex, event.previousIndex);
    this._onChange(this._phxList);
    this._cdr.markForCheck();
  }

  /**
   * Moves an item in the list from one index to another.
   * @param currentIndex - The current index of the item.
   * @param previousIndex - The previous index of the item.
   */
  private moveListItem(currentIndex: number, previousIndex: number): void {
    const contentArray = [...this._phxList];
    moveItemInArray(contentArray, previousIndex, currentIndex);
    this._phxList = contentArray;
  }
}
